<template>
  <page-container>
    <template v-slot:page-fixed-header>
      <span class="text-h5 font-weight-bold ml-3 full-width page-header"
        >{{ $t("task.lblTask") }}
        <v-btn icon x-small class="mx-2 mb-1" @click="openManual()">
          <v-icon color="#404040">mdi-alert-circle-outline</v-icon>
        </v-btn>
      </span>
      <v-spacer></v-spacer>
      <create-task></create-task>
    </template>
    <template v-slot:page-content>
      <task-filter
        v-model="statusFilter"
        @statusFilter="onStatusFilterChange"
        :showMyTask="showMyTask"
      ></task-filter>
      <v-row no-gutters>
        <v-col cols="12" sm="12" style="padding: 10px">
          <v-card flat outlined elevation="3">
            <v-toolbar flat>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-on="on"
                    v-bind="attrs"
                    v-model="search"
                    hide-details
                    prepend-icon="mdi-magnify"
                    single-line
                    :placeholder="$t('task.lblTaskTitle')"
                  ></v-text-field>
                </template>
                <span>{{ $t('task.lblSearchTaskTooltip') }} </span>
              </v-tooltip>
              <v-spacer></v-spacer>
              <v-switch
                v-model="showMyTask"
                :label="$t('task.lblShowMyTask')"
                class="mr-3"
                hide-details
                @change="onMyTaskChange"
              ></v-switch>
            </v-toolbar>
            <task-table-view
              :items="taskListItems"
              :search="search"
              @sendEmail="onSendEmailNotification"
            ></task-table-view>
          </v-card>
        </v-col>
        <message-sheet
          :showMessage="displayMessage"
          :message="message"
          :messageType="messageType"
          @close="displayMessage = false"
        >
        </message-sheet>
        <email-alert-notification
          v-if="showEmailNotificationDialog"
          v-model="showEmailNotificationDialog"
          :emailTemplate="emailTemplate"
          :defaulter="toEmailIds"
          :suggestedList="suggestedEmailIds"
          @send="onEmailSend"
          type="remind"
          :heading="alertHeading"
          section="task"
        ></email-alert-notification>
      </v-row>
    </template>
  </page-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import MessageSheet from "../MessageSheet.vue";
import CreateTask from "./CreateTask.vue";
import TaskFilter from "./TaskFilter.vue";
import TaskTableView from "./TaskTableView.vue";
import SignalRMixin from "../../mixin/SignalRMixin";
import EmailAlertNotification from "../EmailAlertNotification.vue";
import { formatDate } from "@/utils/util";
import { cloneDeep } from "lodash";

export default {
  components: {
    CreateTask,
    MessageSheet,
    TaskTableView,
    TaskFilter,
    EmailAlertNotification,
  },
  name: "TaskManagement",
  mixins: [SignalRMixin],
  data() {
    return {
      manualClick: 0,
      manualWindow: {},
      showEmailNotificationDialog: false,
      showMyTask: false,
      search: "",
      displayMessage: false,
      messageType: "",
      message: "",
      show: true,
      taskItems: [],
      statusFilter: false,
      emailTemplate: {},
      toEmailIds: [],
      suggestedEmailIds: [],
      alertHeading: this.$t("agenda.hNotification"),
      deletedUserIds: [],
    };
  },
  computed: {
    ...mapState({
      taskFilter: (state) => state.task.taskFilter,
      userList: (state) => state.people.userList,
    }),
    ...gettersMap(),
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    tenantName() {
      const users = this.$auth.user[process.env.VUE_APP_TENANT_KEY];
      const tenant = users.find((u) => u.id === this.selectedTenant);
      if (tenant) {
        return tenant.name;
      }
      return "";
    },
    locale() {
      return this.$i18n.locale;
    },
    taskListItems(){
      if(!this.taskItems){
        return []
      }
      return this.taskItems.map(task => ({
          ...task,
          task_status: this.getStatusLabel(task.status),
      }))
    },
    displayDateFormat() {
      return this.locale !== "ja" ? "dd-MM-yyyy h:mm aaa" : "yyyy-MM-dd HH:mm";
    },
    getTaskTemplate() {
      return this.getTemplateByTemplateTypeId("Task", this.locale);
    },
  },
  watch: {
    taskFilter(value) {
      this.statusFilter = value !== "";
    },
    async tasksListItems(items) {
      const waitForUserList = async () => {
        return new Promise((resolve) => {
          const checkUserList = () => {
            if (this.userList.length) {
              resolve();
            } else {
              setTimeout(checkUserList, 100);
            }
          };
          checkUserList();
        });
      };

      await waitForUserList();

      if (items.length) {
        items.map(async (item) => {
          this.getAllAssignees(item.assignees);
        });
        const deletedIds = [...new Set(this.deletedUserIds)];
        await this.getDeletedUsersList(deletedIds);
      }

      this.taskItems = cloneDeep(items);
    },
  },
  async mounted() {
    this.signalRInit(`tasks_${this.selectedTenant}`);
    this.signalRInitDocument(`task_document_${this.selectedTenant}`);
    this.signalRInitAdmin(`admin_${this.selectedTenant}`);
    this.getTasksList();
    this.$root.$on("AlertMessage", async (params) => {
      this.showMessageSheet(params);
      if (params.task) {
        await this.onSendEmailNotification(params.task);
      }
    });
  },
  beforeDestroy() {
    this.$root.$off("AlertMessage");
  },
  methods: {
    ...mapActions({
      getTasksList: "task/getTasksList",
      setMyTasksFilter: "task/setMyTasksFilter",
      setTasksFilter: "task/setTasksFilter",
      getDeletedUsersList: "meeting/getDeletedUsersListTemp",
      getTaskAssigneeDetails: "task/getTaskAssigneeDetails",
      getTasksListWithoutCash: "task/getTasksListWithoutCash",
    }),
    showMessageSheet,
    onMyTaskChange,
    onStatusFilterChange,
    signalRInit,
    signalRInitDocument,
    signalRInitAdmin,
    onSendEmailNotification,
    onEmailSend,
    formatDate,
    getAllAssignees,
    openManual() {
      this.manualClick += 1;
      let height = screen.height;
      let width = screen.width;
      let strWindowFeatures =
        "location=no,toolbar=no,menubar=no,height=" +
        height +
        ",width=" +
        width +
        ",scrollbars=auto,fullscreen=yes";
      let routeData = this.$router.resolve({
        path: "/usermanual",
        query: {
          content:
            this.$i18n.locale == "en"
              ? "Manual_E_Task.pdf"
              : "Manual_J_Task.pdf",
          id: this.$route.name,
        },
      });
      let popup;
      if (
        routeData &&
        (typeof this.manualWindow === "undefined" ||
          this.manualWindow.closed ||
          this.manualClick === 1)
      ) {
        popup = window.open(routeData.href, "_blank", strWindowFeatures);
        this.manualWindow = popup;
        popup.moveTo(0, 0);
      } else {
        this.manualWindow.focus();
      }
    },
    trimHtml(html) {
      return html.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
    },
  },
};

async function onSendEmailNotification(task) {
  try {
    const response = await this.getTaskAssigneeDetails(task.id);
    const dueDate = this.formatDate(
      new Date(task.dueDate),
      this.displayDateFormat
    );
    const startDate = this.formatDate(
      new Date(task.startDate),
      this.displayDateFormat
    );
    const allowedEmails = response.data.allowed ?? [];
    const people = allowedEmails.map((item) => {
      return {
        ...item,
        email: item.registered_email,
      };
    });

    this.toEmailIds = people;
    this.suggestedEmailIds = people;

    // const subject = this.$t("task.taskEmailSubject", {
    //   dueDate: dueDate,
    // });

    // const message = this.$t("task.taskEmailMessage", {
    //   date: dueDate,
    //   task: this.trimHtml(task.title),
    //   tenant: this.tenantName,
    //   status: this.getStatusLabel(task.status),
    // });

    const placeHolderVariables = {
      "{{%date%}}": startDate,
      "{{%tenant%}}": this.tenantName,
      "{{%task_name%}}": this.trimHtml(task.title),
      "{{%status%}}": this.getStatusLabel(task.status),
      "{{%task_due_date%}}": dueDate,
    };

    this.emailTemplate = {
      subject: this.getTaskTemplate.subject.replace(
        /{{%[^}]+%}}/g,
        (match) => placeHolderVariables[match] || ""
      ),
      message: this.getTaskTemplate.body.replace(
        /{{%[^}]+%}}/g,
        (match) => placeHolderVariables[match] || ""
      ),
      mailType: "task-alert",
      dynamicVariables: {
        targetTitle: task.title,
        taskId: task.id,
        targetLinkLabel: this.$t("task.lblTaskLinkLabel"),
        targetLink: `${window.location.origin}/task`,
      },
    };

    this.alertHeading = this.$t("agenda.hNotification");
    this.showEmailNotificationDialog = true;
  } catch (error) {
    console.error("Error sending email notification:", error);
  }
}

function onEmailSend(isSuccess) {
  const message = isSuccess
    ? this.$t("agenda.SendEmailNotification")
    : this.$t("agenda.FaildEmailNotification");

  const messageType = isSuccess ? "success" : "error";

  this.showMessageSheet({
    message,
    messageType,
  });
}

function signalRInit(target) {
  this.signalConnection.on(target, async (response) => {
    if (response !== "") {
      try {
        await this.getTasksList();
      } catch (error) {
        console.log(error, "API error");
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function signalRInitAdmin(target) {
  this.signalConnection.on(target, async (response) => {
    if (response !== "") {
      try {
        await this.getTasksList();
      } catch (error) {
        console.log(error, "API error");
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function signalRInitDocument(target) {
  this.signalConnection.on(target, async (response) => {
    if (response !== "") {
      try {
        await this.getTasksListWithoutCash();
      } catch (error) {
        console.log(error, "API error");
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function onStatusFilterChange(value) {
  if (value) {
    this.setTasksFilter("error");
  } else {
    this.setTasksFilter("");
  }
}

function onMyTaskChange(value) {
  value
    ? this.setMyTasksFilter(this.$auth.user.email)
    : this.setMyTasksFilter(null);
}

function showMessageSheet(params) {
  const { message, messageType } = { ...params };
  this.displayMessage = true;
  this.message = message;
  this.messageType = messageType;
}

async function getAllAssignees(assignees) {
  let localUserList = [...this.userList];
  const userWithIdExists = (id) => localUserList.some((obj) => obj.id === id);
  const unmatchedObjects = assignees.filter((obj) => !userWithIdExists(obj.id));
  const unmatchedIds = unmatchedObjects.map((obj) => obj.id);

  if (unmatchedIds.length) {
    // const result = await this.getDeletedUsersList(unmatchedIds);
    // if (result.length) {
    //   localUserList = localUserList.concat(result);
    // }
    this.deletedUserIds = this.deletedUserIds.concat(unmatchedIds);
  }

  // const matchedUser = filterUsersById(localUserList, assignees);
  // return mapUserDetails(matchedUser);
}

function filterUsersById(userList, assignees) {
  return userList.filter((obj) => objectWithIdExists(obj.id, assignees));
}

function objectWithIdExists(id, assignees) {
  return assignees.some((obj) => obj.id === id);
}

function mapUserDetails(users) {
  return users.map((user) => {
    return {
      id: user.id,
      email: user.display_email ? user.display_email : user.registered_email,
      display_email: user.display_email,
      registered_email: user.registered_email,
      firstName: user.firstName,
      lastName: user.lastName,
      profilePhoto: user.profilePhoto ?? "",
      title: user.title,
    };
  });
}

function gettersMap() {
  return mapGetters({
    tasksListItems: "task/getFilterTaskList",
    getStatusLabel: "task/getStatusLabel",
    getTemplateByTemplateTypeId: "tenant/getTemplateByTemplateTypeId",
  });
}
</script>

<style scoped>
::v-deep .surveydates {
  font-size: smaller !important;
  color: gray;
}
</style>